import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PetsOutlinedIcon from '@mui/icons-material/PetsOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import PhotoAlbumOutlinedIcon from '@mui/icons-material/PhotoAlbumOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import { SvgIconComponent } from '@mui/icons-material';
import { AgeBracket, ChecklistCard } from '../checklistData';
import { colors } from '../../../../theme';
import { track } from '../../analytics';

const CARD_ICONS: Record<string, SvgIconComponent> = {
  school: SchoolOutlinedIcon,
  home: HomeOutlinedIcon,
  menu_book: MenuBookOutlinedIcon,
  restaurant: RestaurantOutlinedIcon,
  favorite: FavoriteBorderOutlinedIcon,
  badge: BadgeOutlinedIcon,
  pets: PetsOutlinedIcon,
  touch_app: TouchAppOutlinedIcon,
  photo_album: PhotoAlbumOutlinedIcon,
  visibility_off: VisibilityOffOutlinedIcon,
  extension: ExtensionOutlinedIcon,
};

function ExternalSiteBadge() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: colors.primary.main,
        bgcolor: colors.primary[50],
        px: 1,
        py: 0.25,
        borderRadius: '12px',
      }}
    >
      <OpenInNewIcon sx={{ fontSize: 12 }} />
      External Site
    </Box>
  );
}

interface ChecklistLessonCardProps {
  card: ChecklistCard;
  ageBracket: AgeBracket;
  /** The task this card sits under, so a lesson's back button can reopen it. */
  taskId: string;
}

function ChecklistLessonCard({ card, ageBracket, taskId }: ChecklistLessonCardProps) {
  const navigate = useNavigate();
  const [imgError, setImgError] = React.useState(false);

  let { title, url } = card;
  if (card.conditional) {
    const variant = ageBracket === 'under6' ? card.under6 : card.over6;
    if (variant) {
      title = variant.title;
      url = variant.url;
    }
  }
  const IconComp = CARD_ICONS[card.icon || 'school'] || SchoolOutlinedIcon;
  const isContain = card.fit === 'contain';

  const open = () => {
    // External partner items (e.g. 24Petwatch / PetPlace insurance) track the destination;
    // lesson/guide cards track the lesson title with the home surface.
    if (card.external) {
      track('checklist_external_link_click', { link_url: url });
    } else {
      track('lessons_title_click', { lesson_name: title, from_where: 'home' });
    }
    if (url.startsWith('/')) {
      // Carry the origin so a lesson's back button returns to the checklist rather
      // than the Lessons & Guides grid — ticket Flag / QA 3.8. The `task` param is
      // what lets Home reopen this section and scroll back to it, instead of
      // landing the parent at the top of the page with a different task expanded.
      navigate(url, { state: { from: `/hub/home?task=${taskId}`, fromWhere: 'checklist' } });
    } else {
      window.open(url, '_blank', 'noopener');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1.25, md: 1.75 },
        alignItems: { xs: 'stretch', md: 'center' },
        bgcolor: colors.grey[50],
        borderRadius: '8px',
        p: { xs: 1.5, md: '12px 14px' },
      }}
    >
      <Box sx={{
        display: 'flex', gap: 1.75, alignItems: 'center', flex: 1, minWidth: 0,
      }}
      >
        <Box
          sx={{
            width: { xs: 64, md: 84 },
            height: { xs: 56, md: 60 },
            borderRadius: '6px',
            bgcolor: isContain ? '#fff' : card.tint,
            border: isContain ? `1px solid ${colors.grey.divider}` : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {card.image && !imgError ? (
            <Box
              component="img"
              src={card.image}
              alt=""
              onError={() => setImgError(true)}
              sx={{
                width: '100%', height: '100%', objectFit: isContain ? 'contain' : 'cover', p: isContain ? 1 : 0,
              }}
            />
          ) : (
            <IconComp sx={{ fontSize: 28, color: card.external ? colors.primary.main : 'rgba(55, 0, 179, 0.45)' }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{title}</Typography>
            {card.external && <ExternalSiteBadge />}
          </Box>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>{card.meta}</Typography>
        </Box>
      </Box>
      <Button
        variant="outlined"
        onClick={open}
        endIcon={card.external ? <OpenInNewIcon sx={{ fontSize: 16 }} /> : undefined}
        sx={{
          width: { xs: '100%', md: 132 },
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 500,
          px: 2,
          py: 1,
          borderRadius: '8px',
          whiteSpace: 'nowrap',
        }}
      >
        {card.cta}
      </Button>
    </Box>
  );
}

export default ChecklistLessonCard;
